// Kleine bouwstenen voor het zijmenu.
import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, CircleAlert, CircleCheck, Info, type LucideIcon } from 'lucide-react';
import type { StatusMessage } from './dataTransfer';

export const focusRing =
  'outline-none focus-visible:ring-2 focus-visible:ring-slate-900/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent dark:focus-visible:ring-slate-100/70';

const btnBase = `inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-4 text-sm font-semibold transition-all duration-150 active:scale-95 disabled:pointer-events-none disabled:opacity-50 ${focusRing}`;

export const btnPrimary = `${btnBase} bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900`;
export const btnTonal = `${btnBase} bg-black/5 text-slate-900 hover:bg-black/10 dark:border dark:border-white/10 dark:bg-slate-800/80 dark:text-slate-100 dark:hover:bg-slate-700/80`;

/** Rij-knop in een lijst (icoon + tekst), monochroom. */
export const rowButton = `flex min-h-12 w-full items-center gap-3 rounded-2xl px-4 text-left text-sm font-semibold text-slate-900 transition-all duration-150 hover:bg-black/5 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:text-slate-100 dark:hover:bg-white/5 ${focusRing}`;

/** Gekozen/niet-gekozen keuzeknop (actief = volle achtergrond, geen dikke rand). */
export function choiceClass(active: boolean): string {
  return `${btnBase} ${
    active
      ? 'bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900'
      : 'bg-black/5 text-slate-800 hover:bg-black/10 dark:border dark:border-white/10 dark:bg-slate-800/80 dark:text-slate-200 dark:hover:bg-slate-700/80'
  }`;
}

interface SectionProps {
  icon: LucideIcon;
  title: string;
  intro?: ReactNode;
  children: ReactNode;
}

/** Menusectie: kop met icoon, scheidingslijn erboven (geen kaart-in-kaart). */
export function Section({ icon: Icon, title, intro, children }: SectionProps) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="space-y-4 border-t border-black/5 pt-6 first:border-t-0 first:pt-0 dark:border-white/10">
      <div className="space-y-1">
        <h3 id={id} className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-slate-100">
          <Icon size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
          {title}
        </h3>
        {intro && <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{intro}</p>}
      </div>
      {children}
    </section>
  );
}

interface DisclosureProps {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
}

/** Inklapbaar blok (gesloten tenzij defaultOpen). */
export function Disclosure({ icon: Icon, label, children, defaultOpen = false }: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <div className="rounded-2xl bg-black/[0.03] dark:bg-white/[0.04]">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className={`${rowButton} justify-between hover:bg-transparent dark:hover:bg-transparent`}
      >
        <span className="flex items-center gap-3 whitespace-nowrap">
          <Icon size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
          {label}
        </span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={`shrink-0 transition-transform duration-150 motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div id={panelId} hidden={!open} className="space-y-4 px-4 pb-4">
        {open && children}
      </div>
    </div>
  );
}

const STATUS_ICON: Record<StatusMessage['kind'], LucideIcon> = {
  info: Info,
  success: CircleCheck,
  error: CircleAlert,
};

const STATUS_COLOR: Record<StatusMessage['kind'], string> = {
  info: 'text-slate-700 dark:text-slate-300',
  success: 'text-emerald-800 dark:text-emerald-300',
  error: 'text-rose-800 dark:text-rose-300',
};

/** Statusregel (live region). Toont niets zolang er geen melding is, maar blijft in de DOM. */
export function StatusLine({ status }: { status: StatusMessage | null }) {
  const Icon = status ? STATUS_ICON[status.kind] : null;
  return (
    <div role="status" aria-live="polite" className="min-h-0 text-sm font-semibold">
      {status && Icon && (
        <p className={`flex items-start gap-2 whitespace-pre-line leading-relaxed ${STATUS_COLOR[status.kind]}`}>
          <Icon size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>{status.text}</span>
        </p>
      )}
    </div>
  );
}
